import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clientsApi, loansApi, savingsAccountsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowDownToLine, ArrowUpFromLine, Receipt, Search, User, DollarSign } from 'lucide-react';

const TellerDashboard = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState(null); // 'deposit' | 'withdraw' | 'repay'
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientLoans, setClientLoans] = useState([]);
  const [clientSavings, setClientSavings] = useState([]);
  const [amount, setAmount] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const searchClients = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setClients([]); return; }
    try {
      const res = await clientsApi.list();
      const filtered = (res.data.clients || res.data || []).filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q.toLowerCase()) ||
        (c.account_number || '').includes(q)
      );
      setClients(filtered.slice(0, 10));
    } catch (e) { console.error(e); }
  };

  const selectClient = async (client) => {
    setSelectedClient(client);
    setClients([]);
    setSearchQuery(`${client.first_name} ${client.last_name}`);
    setSelectedAccount(null);
    setLoading(true);
    try {
      const [loansRes, savingsRes] = await Promise.all([
        loansApi.list(),
        savingsAccountsApi.list(),
      ]);
      const allLoans = loansRes.data.loans || loansRes.data || [];
      const allSavings = savingsRes.data.accounts || savingsRes.data || [];
      setClientLoans(allLoans.filter(l => l.client_id === client.id && l.status === 'active'));
      setClientSavings(allSavings.filter(s => s.client_id === client.id && s.status === 'active'));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!selectedAccount || !amount || parseFloat(amount) <= 0) {
      toast.error('Select an account and enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const amt = parseFloat(amount);
      if (mode === 'deposit') {
        await savingsAccountsApi.deposit(selectedAccount, { amount: amt });
        toast.success(`R ${amt.toFixed(2)} deposited successfully`);
      } else if (mode === 'withdraw') {
        await savingsAccountsApi.withdraw(selectedAccount, { amount: amt });
        toast.success(`R ${amt.toFixed(2)} withdrawn successfully`);
      } else if (mode === 'repay') {
        await loansApi.repay(selectedAccount, amt);
        toast.success(`R ${amt.toFixed(2)} repayment recorded`);
      }
      setAmount('');
      setSelectedAccount(null);
      // Refresh accounts
      if (selectedClient) selectClient(selectedClient);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transaction failed');
    }
    setSubmitting(false);
  };

  const resetTransaction = () => {
    setMode(null);
    setSelectedClient(null);
    setSearchQuery('');
    setClients([]);
    setClientLoans([]);
    setClientSavings([]);
    setAmount('');
    setSelectedAccount(null);
  };

  const fmt = (v) => `R ${(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

  return (
    <div className="max-w-2xl mx-auto" data-testid="teller-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1f1f1f]">Teller Terminal</h1>
        <p className="text-sm text-gray-500">Welcome, {user?.name || 'Teller'}. Select a transaction type to begin.</p>
      </div>

      {/* Transaction Type Selection */}
      {!mode && (
        <div className="grid grid-cols-3 gap-4" data-testid="teller-action-grid">
          <button onClick={() => setMode('deposit')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all flex flex-col items-center gap-3"
            data-testid="teller-deposit-btn">
            <div className="w-14 h-14 bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
              <ArrowDownToLine className="w-7 h-7 text-emerald-600" />
            </div>
            <span className="text-sm font-semibold text-[#1f1f1f]">Deposit</span>
            <span className="text-xs text-gray-400">Savings deposit</span>
          </button>

          <button onClick={() => setMode('withdraw')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-amber-400 hover:bg-amber-50 transition-all flex flex-col items-center gap-3"
            data-testid="teller-withdraw-btn">
            <div className="w-14 h-14 bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
              <ArrowUpFromLine className="w-7 h-7 text-amber-600" />
            </div>
            <span className="text-sm font-semibold text-[#1f1f1f]">Withdraw</span>
            <span className="text-xs text-gray-400">Savings withdrawal</span>
          </button>

          <button onClick={() => setMode('repay')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex flex-col items-center gap-3"
            data-testid="teller-repay-btn">
            <div className="w-14 h-14 bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Receipt className="w-7 h-7 text-blue-600" />
            </div>
            <span className="text-sm font-semibold text-[#1f1f1f]">Loan Repayment</span>
            <span className="text-xs text-gray-400">Record payment</span>
          </button>
        </div>
      )}

      {/* Transaction Flow */}
      {mode && (
        <div className="bg-white border border-gray-200 p-6" data-testid="teller-transaction-form">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center ${
                mode === 'deposit' ? 'bg-emerald-100' : mode === 'withdraw' ? 'bg-amber-100' : 'bg-blue-100'
              }`}>
                {mode === 'deposit' && <ArrowDownToLine className="w-5 h-5 text-emerald-600" />}
                {mode === 'withdraw' && <ArrowUpFromLine className="w-5 h-5 text-amber-600" />}
                {mode === 'repay' && <Receipt className="w-5 h-5 text-blue-600" />}
              </div>
              <div>
                <h2 className="font-semibold text-[#1f1f1f] capitalize">{mode === 'repay' ? 'Loan Repayment' : mode}</h2>
                <p className="text-xs text-gray-400">{mode === 'deposit' ? 'Credit to savings' : mode === 'withdraw' ? 'Debit from savings' : 'Payment towards loan'}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={resetTransaction} data-testid="teller-cancel-btn">Cancel</Button>
          </div>

          {/* Client Search */}
          <div className="mb-5">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Client</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => searchClients(e.target.value)}
                placeholder="Search by name or account number..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                data-testid="teller-client-search"
              />
              {clients.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 shadow-lg z-10 max-h-48 overflow-y-auto">
                  {clients.map(c => (
                    <button key={c.id} onClick={() => selectClient(c)}
                      className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 border-b last:border-0"
                      data-testid={`teller-client-option-${c.id}`}>
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-gray-400">{c.account_number || 'No account #'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedClient && (
              <div className="mt-2 px-3 py-2 bg-gray-50 border border-gray-100 flex items-center gap-2">
                <User className="w-4 h-4 text-[#C5A059]" />
                <span className="text-sm font-medium text-[#1f1f1f]">{selectedClient.first_name} {selectedClient.last_name}</span>
                <span className="text-xs text-gray-400 ml-auto">{selectedClient.account_number}</span>
              </div>
            )}
          </div>

          {/* Account Selection */}
          {selectedClient && !loading && (
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {mode === 'repay' ? 'Select Loan' : 'Select Savings Account'}
              </label>
              {mode === 'repay' ? (
                clientLoans.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No active loans found for this client.</p>
                ) : (
                  <div className="space-y-2">
                    {clientLoans.map(l => (
                      <button key={l.id} onClick={() => setSelectedAccount(l.id)}
                        className={`w-full text-left p-3 border transition-colors ${
                          selectedAccount === l.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`teller-loan-${l.id}`}>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Loan #{l.loan_number}</span>
                          <span className="text-sm font-bold text-[#1f1f1f]">{fmt(l.outstanding_balance)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{l.product_name || 'Loan'} - Outstanding</p>
                      </button>
                    ))}
                  </div>
                )
              ) : (
                clientSavings.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No active savings accounts found for this client.</p>
                ) : (
                  <div className="space-y-2">
                    {clientSavings.map(s => (
                      <button key={s.id} onClick={() => setSelectedAccount(s.id)}
                        className={`w-full text-left p-3 border transition-colors ${
                          selectedAccount === s.id ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`teller-savings-${s.id}`}>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Account #{s.account_number}</span>
                          <span className="text-sm font-bold text-[#1f1f1f]">{fmt(s.balance)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{s.product_name || 'Savings'} - Balance</p>
                      </button>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {loading && (
            <div className="py-4 text-center text-sm text-gray-400">Loading accounts...</div>
          )}

          {/* Amount Input */}
          {selectedAccount && (
            <div className="mb-6">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Amount (R)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                  data-testid="teller-amount-input"
                />
              </div>
            </div>
          )}

          {/* Submit */}
          {selectedAccount && amount && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full py-3 text-white font-medium ${
                mode === 'deposit' ? 'bg-emerald-600 hover:bg-emerald-700' :
                mode === 'withdraw' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-blue-600 hover:bg-blue-700'
              }`}
              data-testid="teller-submit-btn"
            >
              {submitting ? 'Processing...' : `Confirm ${mode === 'repay' ? 'Repayment' : mode.charAt(0).toUpperCase() + mode.slice(1)} — ${fmt(parseFloat(amount) || 0)}`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TellerDashboard;
